import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import './SchoolDashboard.css';
import './Routes.css';
import { useBranchFilter } from '../../hooks/useBranchFilter';

function Routes() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [loadingRoutes, setLoadingRoutes] = useState(true);
  const [viewingRoute, setViewingRoute] = useState(null);
  const [editingRoute, setEditingRoute] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [pickupLocations, setPickupLocations] = useState([]);
  const { selectedBranchId, handleBranchChange, refreshTrigger } = useBranchFilter();
  const [editFormData, setEditFormData] = useState({
    vehicle_id: '',
    route_type: '',
    route_name: '',
    selected_locations: []
  });
  const [editErrors, setEditErrors] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const sd = localStorage.getItem('schoolData');
    if (!sd) {
      navigate('/schoollogin');
      return;
    }
    setSchoolData(JSON.parse(sd));
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (schoolData?.id) {
      fetchRoutes();
      fetchVehicles();
      fetchPickupLocations();
    }
  }, [schoolData?.id, selectedBranchId, refreshTrigger]);

  useEffect(() => {
    if (selectedBranchId !== undefined) {
      setRoutes([]);
    }
  }, [selectedBranchId]);

  const fetchRoutes = async () => {
    try {
      setLoadingRoutes(true);
      
      // Fetch routes
      let routesQuery = supabase
        .from('routes')
        .select('*')
        .eq('school_id', schoolData.id);

      if (selectedBranchId != null) {
        routesQuery = routesQuery.eq('branch_id', selectedBranchId);
      }

      const { data: routesData, error: routesError } = await routesQuery.order('created_at', { ascending: false });

      if (routesError) {
        console.error('Error fetching routes:', routesError);
        alert('Error loading routes: ' + routesError.message);
        return;
      }

      // For each route, fetch vehicle and pickup locations
      const routesWithDetails = await Promise.all(
        (routesData || []).map(async (route) => {
          // Fetch vehicle information
          const { data: vehicleData, error: vehicleError } = await supabase
            .from('transport_vehicles')
            .select('id, bus_number, driver_name')
            .eq('id', route.vehicle_id)
            .single();

          if (vehicleError) {
            console.error('Error fetching vehicle:', vehicleError);
          }

          // Fetch pickup locations for this route
          const { data: routeLocationData, error: locationError } = await supabase
            .from('route_pickup_locations')
            .select('sequence_order, pickup_location_id')
            .eq('route_id', route.id)
            .order('sequence_order', { ascending: true });

          let pickupLocations = [];
          if (routeLocationData && routeLocationData.length > 0) {
            const locationIds = routeLocationData.map(item => item.pickup_location_id);
            
            const { data: locationDetails, error: detailsError } = await supabase
              .from('pickup_locations')
              .select('id, location_name, address')
              .in('id', locationIds);

            if (!detailsError && locationDetails) {
              // Map locations with their sequence order
              pickupLocations = routeLocationData.map(routeLoc => {
                const location = locationDetails.find(loc => loc.id === routeLoc.pickup_location_id);
                return location ? {
                  ...location,
                  sequence_order: routeLoc.sequence_order
                } : null;
              }).filter(Boolean);
            }
          }

          return {
            ...route,
            vehicle: vehicleData || null,
            pickup_locations: pickupLocations
          };
        })
      );

      setRoutes(routesWithDetails);
    } catch (err) {
      console.error('Error:', err);
      alert('An error occurred while loading routes.');
    } finally {
      setLoadingRoutes(false);
    }
  };

  const handleLogout = () => navigate('/schoollogin');

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getRouteTypeBadgeColor = (routeType) => {
    switch (routeType) {
      case '1 Way Morning':
        return { bg: '#DBEAFE', color: '#1E40AF' };
      case '1 Way Evening':
        return { bg: '#FEF3C7', color: '#92400E' };
      case '2 Way':
        return { bg: '#D1FAE5', color: '#065F46' };
      default:
        return { bg: '#F3F4F6', color: '#374151' };
    }
  };

  const handleViewRoute = (route) => {
    setViewingRoute(route);
  };

  const fetchVehicles = async () => {
    try {
      let query = supabase
        .from('transport_vehicles')
        .select('id, bus_number, capacity, driver_name')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .order('bus_number', { ascending: true });

      if (selectedBranchId != null) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching vehicles:', error);
        return;
      }

      setVehicles(data || []);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchPickupLocations = async () => {
    try {
      let query = supabase
        .from('pickup_locations')
        .select('id, location_name, address')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .order('location_name', { ascending: true });

      if (selectedBranchId != null) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching pickup locations:', error);
        return;
      }

      setPickupLocations(data || []);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleEditRoute = (route) => {
    setEditingRoute(route);
    // Pre-fill form with route data
    setEditFormData({
      vehicle_id: route.vehicle_id.toString(),
      route_type: route.route_type,
      route_name: route.route_name || '',
      selected_locations: route.pickup_locations ? route.pickup_locations.map(loc => loc.id) : []
    });
    setEditErrors({});
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (editErrors[name]) {
      setEditErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleEditLocationToggle = (locationId) => {
    setEditFormData(prev => {
      const selected = prev.selected_locations;
      const isSelected = selected.includes(locationId);
      
      return {
        ...prev,
        selected_locations: isSelected
          ? selected.filter(id => id !== locationId)
          : [...selected, locationId]
      };
    });
    
    if (editErrors.selected_locations) {
      setEditErrors(prev => ({
        ...prev,
        selected_locations: ''
      }));
    }
  };

  const validateEditForm = () => {
    const newErrors = {};
    
    if (!editFormData.vehicle_id) {
      newErrors.vehicle_id = 'Please select a vehicle';
    }
    
    if (!editFormData.route_type) {
      newErrors.route_type = 'Please select a route type';
    }
    
    if (editFormData.selected_locations.length === 0) {
      newErrors.selected_locations = 'Please select at least one pickup location';
    }
    
    setEditErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdateRoute = async (e) => {
    e.preventDefault();
    
    if (!validateEditForm()) {
      return;
    }
    
    setIsUpdating(true);
    
    try {
      // Update the route
      const routeData = {
        vehicle_id: parseInt(editFormData.vehicle_id, 10),
        route_type: editFormData.route_type,
        route_name: editFormData.route_name.trim() || null,
        updated_at: new Date().toISOString(),
        branch_id: selectedBranchId
      };
      
      const { error: routeError } = await supabase
        .from('routes')
        .update(routeData)
        .eq('id', editingRoute.id);
      
      if (routeError) {
        console.error('Error updating route:', routeError);
        alert('Error updating route: ' + routeError.message);
        return;
      }
      
      // Delete existing route_pickup_locations
      const { error: deleteError } = await supabase
        .from('route_pickup_locations')
        .delete()
        .eq('route_id', editingRoute.id);
      
      if (deleteError) {
        console.error('Error deleting old pickup locations:', deleteError);
      }
      
      // Insert new route_pickup_locations
      if (editFormData.selected_locations.length > 0) {
        const routeLocationData = editFormData.selected_locations.map((locationId, index) => ({
          route_id: editingRoute.id,
          pickup_location_id: locationId,
          sequence_order: index + 1
        }));
        
        const { error: locationError } = await supabase
          .from('route_pickup_locations')
          .insert(routeLocationData);
        
        if (locationError) {
          console.error('Error updating pickup locations:', locationError);
          alert('Error updating pickup locations: ' + locationError.message);
          return;
        }
      }
      
      // Refresh routes list
      await fetchRoutes();
      setEditingRoute(null);
      alert('Route updated successfully!');
      
    } catch (err) {
      console.error('Error:', err);
      alert('An error occurred while updating the route. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteRoute = async (route) => {
    const routeName = route.route_name || `Route #${route.id}`;
    if (!window.confirm(`Are you sure you want to delete "${routeName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      let deleteQuery = supabase
        .from('routes')
        .delete()
        .eq('id', route.id);

      if (selectedBranchId != null) {
        deleteQuery = deleteQuery.eq('branch_id', selectedBranchId);
      }

      const { error } = await deleteQuery;

      if (error) {
        console.error('Error deleting route:', error);
        alert('Error deleting route: ' + error.message);
        return;
      }

      // Refresh the list
      fetchRoutes();
      alert('Route deleted successfully!');
    } catch (err) {
      console.error('Error:', err);
      alert('An error occurred while deleting the route.');
    }
  };

  if (loading) {
    return (
      <div className="school-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      
      <div className="main-content">
        <div className="routes-page-container">
          <div className="routes-container">
            {/* Page Header Card */}
            <div className="routes-header-card">
              <div className="routes-header-icon">🗺️</div>
              <h1 className="routes-header-title">All Routes</h1>
              <div className="routes-header-subtitle">
                <span>Total Routes:</span>
                <span className="routes-count-badge">{routes.length}</span>
              </div>
              {routes.length > 0 && (
                <div className="routes-add-button-container-top">
                  <button
                    className="routes-add-button-top"
                    onClick={() => navigate('/school/add-route')}
                  >
                    <span className="routes-add-button-icon">➕</span>
                    <span>Add New Route</span>
                  </button>
                </div>
              )}
            </div>

            {loadingRoutes ? (
              <div className="loading-container" style={{ padding: '2rem' }}>
                <div className="loading-spinner"></div>
                <p>Loading routes...</p>
              </div>
            ) : routes.length === 0 ? (
              <div className="routes-empty-state">
                <div className="routes-empty-icon">🗺️</div>
                <p className="routes-empty-message">No routes found</p>
                <button
                  className="routes-empty-button"
                  onClick={() => navigate('/school/add-route')}
                >
                  Add Your First Route
                </button>
              </div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="routes-table-container">
                  <table className="routes-table">
                    <thead>
                      <tr>
                        <th className="route-name-header">Route Name</th>
                        <th className="vehicle-header">Vehicle</th>
                        <th className="route-type-header">Route Type</th>
                        <th className="pickup-locations-header">Pickup Locations</th>
                        <th className="status-header">Status</th>
                        <th className="created-date-header">Created Date</th>
                        <th className="actions-header">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {routes.map((route) => {
                        const routeTypeClass = route.route_type === '2 Way' ? 'two-way' : 'one-way';
                        return (
                          <tr key={route.id}>
                            <td>
                              <div className="route-name-cell">
                                <span className="route-name-icon">🗺️</span>
                                <span>{route.route_name || `Route #${route.id}`}</span>
                              </div>
                            </td>
                            <td>
                              <div className="vehicle-info-cell">
                                <div className="vehicle-number">
                                  <span className="vehicle-number-icon">🚌</span>
                                  <span>{route.vehicle?.bus_number || 'N/A'}</span>
                                </div>
                                {route.vehicle?.driver_name && (
                                  <div className="driver-name">
                                    <span className="driver-name-icon">👤</span>
                                    <span>Driver: {route.vehicle.driver_name}</span>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <span className={`route-type-badge ${routeTypeClass}`}>
                                {route.route_type}
                              </span>
                            </td>
                            <td>
                              {route.pickup_locations && route.pickup_locations.length > 0 ? (
                                <div className="pickup-locations-cell">
                                  {route.pickup_locations.map((location, idx) => (
                                    <div key={location.id || idx} className="location-card">
                                      <div className="location-number-name">
                                        <span className="location-icon">📍</span>
                                        <span>{location.sequence_order || idx + 1}. {location.location_name}</span>
                                      </div>
                                      {location.address && (
                                        <div className="location-address">{location.address}</div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>No locations</span>
                              )}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <span className={`status-badge ${route.is_active ? 'active' : 'inactive'}`}>
                                {route.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="created-date-cell">
                              <span className="created-date-icon">📅</span>
                              <span>{formatDate(route.created_at)}</span>
                            </td>
                            <td className="actions-cell">
                              <div className="routes-actions-container">
                                <button
                                  className="route-action-btn view-btn"
                                  onClick={() => handleViewRoute(route)}
                                  title="View Details"
                                >
                                  👁️
                                </button>
                                <button
                                  className="route-action-btn edit-btn"
                                  onClick={() => handleEditRoute(route)}
                                  title="Edit Route"
                                >
                                  ✏️
                                </button>
                                <button
                                  className="route-action-btn delete-btn"
                                  onClick={() => handleDeleteRoute(route)}
                                  title="Delete Route"
                                >
                                  🗑️
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="routes-mobile-cards">
                  {routes.map((route) => {
                    const routeTypeClass = route.route_type === '2 Way' ? 'two-way' : 'one-way';
                    return (
                      <div key={route.id} className="route-mobile-card">
                        <div className="route-mobile-card-header">
                          <div className="route-mobile-name">
                            <span>🗺️</span>
                            <span>{route.route_name || `Route #${route.id}`}</span>
                          </div>
                          <span className={`route-mobile-type-badge ${routeTypeClass}`}>
                            {route.route_type}
                          </span>
                        </div>
                        <div className="route-mobile-card-body">
                          <div className="route-mobile-section">
                            <span className="route-mobile-section-label">Vehicle</span>
                            <div className="route-mobile-vehicle-info">
                              <div className="vehicle-number">
                                <span className="vehicle-number-icon">🚌</span>
                                <span>{route.vehicle?.bus_number || 'N/A'}</span>
                              </div>
                              {route.vehicle?.driver_name && (
                                <div className="driver-name">
                                  <span className="driver-name-icon">👤</span>
                                  <span>Driver: {route.vehicle.driver_name}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="route-mobile-section">
                            <span className="route-mobile-section-label">Pickup Locations</span>
                            {route.pickup_locations && route.pickup_locations.length > 0 ? (
                              <div className="route-mobile-locations">
                                {route.pickup_locations.map((location, idx) => (
                                  <div key={location.id || idx} className="route-mobile-location-card">
                                    <div className="location-number-name">
                                      <span className="location-icon">📍</span>
                                      <span>{location.sequence_order || idx + 1}. {location.location_name}</span>
                                    </div>
                                    {location.address && (
                                      <div className="location-address">{location.address}</div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>No locations</span>
                            )}
                          </div>
                          <div className="route-mobile-section">
                            <span className="route-mobile-section-label">Status</span>
                            <span className={`status-badge ${route.is_active ? 'active' : 'inactive'}`}>
                              {route.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <div className="route-mobile-section">
                            <span className="route-mobile-section-label">Created Date</span>
                            <div className="created-date-cell">
                              <span className="created-date-icon">📅</span>
                              <span>{formatDate(route.created_at)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="route-mobile-actions">
                          <button
                            className="route-mobile-action-btn view-btn"
                            onClick={() => handleViewRoute(route)}
                          >
                            <span>👁️</span>
                            <span>View</span>
                          </button>
                          <button
                            className="route-mobile-action-btn edit-btn"
                            onClick={() => handleEditRoute(route)}
                          >
                            <span>✏️</span>
                            <span>Edit</span>
                          </button>
                          <button
                            className="route-mobile-action-btn delete-btn"
                            onClick={() => handleDeleteRoute(route)}
                          >
                            <span>🗑️</span>
                            <span>Delete</span>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}


            {/* View Route Modal */}
            {viewingRoute && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
              }}
              onClick={() => setViewingRoute(null)}
              >
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '2rem',
                  maxWidth: '700px',
                  width: '90%',
                  maxHeight: '80vh',
                  overflowY: 'auto',
                  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                }}
                onClick={(e) => e.stopPropagation()}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1.5rem'
                  }}>
                    <h2 style={{ margin: 0, color: '#1F2937' }}>Route Details</h2>
                    <button
                      onClick={() => setViewingRoute(null)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        fontSize: '1.5rem',
                        cursor: 'pointer',
                        color: '#6B7280'
                      }}
                    >
                      ×
                    </button>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div>
                      <strong style={{ color: '#6B7280', fontSize: '0.875rem' }}>Route Name:</strong>
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '1.1rem', fontWeight: '600' }}>
                        {viewingRoute.route_name || `Route #${viewingRoute.id}`}
                      </p>
                    </div>
                    
                    <div>
                      <strong style={{ color: '#6B7280', fontSize: '0.875rem' }}>Vehicle:</strong>
                      <div style={{ marginTop: '0.25rem' }}>
                        <p style={{ margin: 0, fontWeight: '500' }}>
                          {viewingRoute.vehicle?.bus_number || 'N/A'}
                        </p>
                        {viewingRoute.vehicle?.driver_name && (
                          <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem', color: '#6B7280' }}>
                            Driver: {viewingRoute.vehicle.driver_name}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <strong style={{ color: '#6B7280', fontSize: '0.875rem' }}>Route Type:</strong>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '0.375rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.85rem',
                          fontWeight: '500',
                          backgroundColor: getRouteTypeBadgeColor(viewingRoute.route_type).bg,
                          color: getRouteTypeBadgeColor(viewingRoute.route_type).color,
                          marginLeft: '0.5rem'
                        }}
                      >
                        {viewingRoute.route_type}
                      </span>
                    </div>
                    
                    <div>
                      <strong style={{ color: '#6B7280', fontSize: '0.875rem' }}>Pickup Locations:</strong>
                      {viewingRoute.pickup_locations && viewingRoute.pickup_locations.length > 0 ? (
                        <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {viewingRoute.pickup_locations.map((location, idx) => (
                            <div 
                              key={location.id || idx}
                              style={{
                                padding: '0.75rem',
                                background: '#F9FAFB',
                                borderRadius: '8px',
                                border: '1px solid #E5E7EB'
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ 
                                  fontWeight: '600', 
                                  color: '#1F2937',
                                  minWidth: '24px'
                                }}>
                                  {location.sequence_order || idx + 1}.
                                </span>
                                <div>
                                  <div style={{ fontWeight: '500', color: '#374151' }}>
                                    {location.location_name}
                                  </div>
                                  {location.address && (
                                    <div style={{ 
                                      fontSize: '0.85rem', 
                                      color: '#6B7280',
                                      marginTop: '0.25rem'
                                    }}>
                                      {location.address}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p style={{ margin: '0.25rem 0 0 0', color: '#9CA3AF', fontStyle: 'italic' }}>
                          No pickup locations assigned
                        </p>
                      )}
                    </div>
                    
                    <div>
                      <strong style={{ color: '#6B7280', fontSize: '0.875rem' }}>Status:</strong>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.85rem',
                          fontWeight: '500',
                          backgroundColor: viewingRoute.is_active ? '#D1FAE5' : '#FEE2E2',
                          color: viewingRoute.is_active ? '#065F46' : '#991B1B',
                          marginLeft: '0.5rem'
                        }}
                      >
                        {viewingRoute.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    
                    <div>
                      <strong style={{ color: '#6B7280', fontSize: '0.875rem' }}>Created Date:</strong>
                      <p style={{ margin: '0.25rem 0 0 0' }}>{formatDate(viewingRoute.created_at)}</p>
                    </div>
                  </div>
                  
                  <div style={{
                    marginTop: '1.5rem',
                    display: 'flex',
                    gap: '0.75rem',
                    justifyContent: 'flex-end'
                  }}>
                    <button
                      onClick={() => {
                        setViewingRoute(null);
                        handleEditRoute(viewingRoute);
                      }}
                      style={{
                        background: '#10B981',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem 1rem',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setViewingRoute(null)}
                      style={{
                        background: '#6B7280',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem 1rem',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Edit Route Modal */}
            {editingRoute && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
              }}
              onClick={() => setEditingRoute(null)}
              >
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '2rem',
                  maxWidth: '800px',
                  width: '90%',
                  maxHeight: '90vh',
                  overflowY: 'auto',
                  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                }}
                onClick={(e) => e.stopPropagation()}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1.5rem',
                    paddingBottom: '1rem',
                    borderBottom: '1px solid #E5E7EB'
                  }}>
                    <h2 style={{ margin: 0, color: '#1F2937', fontSize: '1.5rem', fontWeight: '600' }}>Edit Route</h2>
                    <button
                      onClick={() => setEditingRoute(null)}
                      style={{
                        background: '#F3F4F6',
                        border: 'none',
                        fontSize: '1.5rem',
                        cursor: 'pointer',
                        color: '#6B7280',
                        width: '32px',
                        height: '32px',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s',
                        fontWeight: 'bold',
                        lineHeight: '1'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#E5E7EB';
                        e.currentTarget.style.color = '#374151';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#F3F4F6';
                        e.currentTarget.style.color = '#6B7280';
                      }}
                      title="Close"
                    >
                      ×
                    </button>
                  </div>
                  
                  <form onSubmit={handleUpdateRoute}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                          Select Vehicle <span style={{ color: '#EF4444' }}>*</span>
                        </label>
                        <select
                          name="vehicle_id"
                          value={editFormData.vehicle_id}
                          onChange={handleEditInputChange}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: editErrors.vehicle_id ? '2px solid #EF4444' : '1px solid #D1D5DB',
                            borderRadius: '8px',
                            fontSize: '0.95rem',
                            backgroundColor: '#FFFFFF'
                          }}
                        >
                          <option value="">-- Select a vehicle --</option>
                          {vehicles.map((vehicle) => (
                            <option key={vehicle.id} value={vehicle.id}>
                              {vehicle.bus_number} {vehicle.driver_name ? `(Driver: ${vehicle.driver_name})` : ''}
                            </option>
                          ))}
                        </select>
                        {editErrors.vehicle_id && (
                          <span style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                            {editErrors.vehicle_id}
                          </span>
                        )}
                      </div>
                      
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                          Route Type <span style={{ color: '#EF4444' }}>*</span>
                        </label>
                        <select
                          name="route_type"
                          value={editFormData.route_type}
                          onChange={handleEditInputChange}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: editErrors.route_type ? '2px solid #EF4444' : '1px solid #D1D5DB',
                            borderRadius: '8px',
                            fontSize: '0.95rem',
                            backgroundColor: '#FFFFFF'
                          }}
                        >
                          <option value="">-- Select route type --</option>
                          <option value="1 Way Morning">1 Way Morning</option>
                          <option value="1 Way Evening">1 Way Evening</option>
                          <option value="2 Way">2 Way</option>
                        </select>
                        {editErrors.route_type && (
                          <span style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                            {editErrors.route_type}
                          </span>
                        )}
                      </div>
                      
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                          Route Name (Optional)
                        </label>
                        <input
                          type="text"
                          name="route_name"
                          value={editFormData.route_name}
                          onChange={handleEditInputChange}
                          placeholder="e.g., Route A, North Route, etc."
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid #D1D5DB',
                            borderRadius: '8px',
                            fontSize: '0.95rem'
                          }}
                        />
                      </div>
                      
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                          Select Pickup Locations <span style={{ color: '#EF4444' }}>*</span>
                        </label>
                        {editErrors.selected_locations && (
                          <span style={{ color: '#EF4444', fontSize: '0.875rem', display: 'block', marginBottom: '0.5rem' }}>
                            {editErrors.selected_locations}
                          </span>
                        )}
                        <div style={{
                          border: editErrors.selected_locations ? '2px solid #EF4444' : '1px solid #E5E7EB',
                          borderRadius: '12px',
                          padding: '1rem',
                          maxHeight: '300px',
                          overflowY: 'auto',
                          background: '#FFFFFF',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                        }}>
                          {pickupLocations.map((location, index) => {
                            const isSelected = editFormData.selected_locations.includes(location.id);
                            return (
                              <div
                                key={location.id}
                                onClick={() => handleEditLocationToggle(location.id)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  padding: '1rem',
                                  marginBottom: index < pickupLocations.length - 1 ? '0.75rem' : '0',
                                  cursor: 'pointer',
                                  borderRadius: '10px',
                                  background: isSelected ? '#EFF6FF' : '#FFFFFF',
                                  border: isSelected ? '2px solid #3B82F6' : '1px solid #E5E7EB',
                                  transition: 'all 0.2s ease',
                                  position: 'relative'
                                }}
                                onMouseEnter={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.background = '#F9FAFB';
                                    e.currentTarget.style.borderColor = '#D1D5DB';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.background = '#FFFFFF';
                                    e.currentTarget.style.borderColor = '#E5E7EB';
                                  }
                                }}
                              >
                                <div style={{
                                  width: '24px',
                                  height: '24px',
                                  minWidth: '24px',
                                  marginRight: '1rem',
                                  marginTop: '0.125rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '6px',
                                  border: isSelected ? '2px solid #3B82F6' : '2px solid #D1D5DB',
                                  background: isSelected ? '#3B82F6' : '#FFFFFF',
                                  transition: 'all 0.2s ease'
                                }}>
                                  {isSelected && (
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M11.6667 3.5L5.25 9.91667L2.33334 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  )}
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ 
                                    fontWeight: '500',
                                    fontSize: '0.95rem',
                                    color: '#1F2937',
                                    marginBottom: '0.375rem',
                                    lineHeight: '1.4'
                                  }}>
                                    {location.location_name}
                                  </div>
                                  <div style={{ 
                                    fontSize: '0.875rem', 
                                    color: '#6B7280',
                                    lineHeight: '1.5'
                                  }}>
                                    {location.address}
                                  </div>
                                </div>
                                {isSelected && (
                                  <div style={{
                                    position: 'absolute',
                                    top: '0.5rem',
                                    right: '0.5rem',
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    background: '#3B82F6'
                                  }} />
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <div style={{
                          marginTop: '1rem',
                          padding: '0.75rem 1rem',
                          background: editFormData.selected_locations.length > 0 ? '#EFF6FF' : '#F9FAFB',
                          borderRadius: '8px',
                          border: editFormData.selected_locations.length > 0 ? '1px solid #BFDBFE' : '1px solid #E5E7EB'
                        }}>
                          <span style={{ 
                            fontSize: '0.875rem', 
                            color: editFormData.selected_locations.length > 0 ? '#1E40AF' : '#6B7280',
                            fontWeight: editFormData.selected_locations.length > 0 ? '600' : '500'
                          }}>
                            {editFormData.selected_locations.length > 0 
                              ? `✓ ${editFormData.selected_locations.length} location${editFormData.selected_locations.length > 1 ? 's' : ''} selected`
                              : 'No locations selected'
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div style={{
                      marginTop: '2rem',
                      display: 'flex',
                      gap: '0.75rem',
                      justifyContent: 'flex-end'
                    }}>
                      <button
                        type="button"
                        onClick={() => setEditingRoute(null)}
                        style={{
                          background: '#6B7280',
                          color: 'white',
                          border: 'none',
                          padding: '0.75rem 1.5rem',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isUpdating}
                        style={{
                          background: '#FF8C42',
                          color: 'white',
                          border: 'none',
                          padding: '0.75rem 1.5rem',
                          borderRadius: '6px',
                          cursor: isUpdating ? 'not-allowed' : 'pointer',
                          fontWeight: '500',
                          opacity: isUpdating ? 0.6 : 1
                        }}
                      >
                        {isUpdating ? 'Updating Route...' : 'Update Route'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

export default Routes;

